<script setup>
import { useShopStore } from '~/stores/shop'
import { useReviewsStore } from '~/stores/reviews'
import { getMetaData, getStructuredData } from '~/utils/bookSeo'
import BookBasicInfo from '~/components/books/BookBasicInfo'
import BookBuySection from '~/components/shop/BookBuySection'
import BookDescription from '~/components/books/BookDescription'
import BookCharacteristics from '~/components/books/BookCharacteristics'
import RelatedBooksCarousel from '~/components/books/RelatedBooksCarousel'
import BaseQuote from '~/components/BaseQuote'
import ReviewsSection from '~/components/reviews/ReviewsSection'
import WarningAlert from '~/components/alerts/WarningAlert'
import { PAGES, TRACKING_LISTS } from '~/utils/constants'
import { useBook } from '~/composables/useBook'
import EbookPreview from '~/components/ebook/EbookPreview'

const { $label, $gtmTracking, $sentryCaptureException } = useNuxtApp()
const route = useRoute()
const { slug } = route.params
const reviewsStore = useReviewsStore()
const shopStore = useShopStore()

const { data: book, error } = await useAsyncData(slug, () => {
  reviewsStore.clearReviews()
  return shopStore.getShopBookDetail(slug)
})

if (error.value) {
  $sentryCaptureException(error.value)

  if (error.value.statusCode) {
    throw createError({
      fatal: true,
      statusCode: error.value.statusCode,
      statusMessage: error.value.statusMessage
    })
  }
}

useHead({
  title: `${book.value.title} - ${book.value.author?.name} • ebook • Shop • Bookchoice`,
  meta: getMetaData(book.value, route),
  script: [
    { type: 'application/ld+json', innerHTML: getStructuredData(book.value) }
  ]
})

const breadcrumb = computed(() => {
  const query = new URLSearchParams(route.query)
  const queryString = query.size > 0 ? '?' + query.toString() : ''
  const link = PAGES.shop + queryString

  return {
    link,
    text: $label('shop.backToShopBreadcrumb')
  }
})
const bookCharacteristics = book.value?.characteristics
const firstQuote = ref(book.value?.quotes && book.value.quotes[0])
const secondQuote = ref(book.value?.quotes && book.value.quotes[1])
const availableForPurchase =
  book.value?.availableForPurchase && book.value.salePrice > 0

const { genreToKey } = useBook()
const genre = genreToKey(book.value.genres)
const bgClassWithGenre = 'background-section--' + genre

onMounted(() => {
  try {
    const item = {
      ...book.value,
      price: book.value.listPrice,
      discount: book.value.listPrice - book.value.salePrice,
      name: book.value.title
    }

    $gtmTracking.viewItem({
      item,
      value: book.value.salePrice,
      ...TRACKING_LISTS.shop
    })
    // eslint-disable-next-line no-empty
  } catch {}
})
</script>

<template>
  <div v-if="book">
    <WarningAlert
      v-if="!availableForPurchase"
      class="mb-0 d-md-flex justify-content-md-center"
    >
      <!--eslint-disable-next-line vue/no-v-html-->
      <span v-html="$label('shop.bookNotAvailable')" />
    </WarningAlert>

    <BookBasicInfo :book="book" :breadcrumb="breadcrumb">
      <template v-if="book.ebookPreviewFileUrl" #book-download>
        <EbookPreview :book="book" :file-url="book.ebookPreviewFileUrl" />
      </template>
      <template v-if="availableForPurchase" #book-actions>
        <BookBuySection :book="book" />
      </template>
    </BookBasicInfo>

    <BaseQuote v-if="firstQuote" :quote="firstQuote" />

    <BookCharacteristics
      v-if="bookCharacteristics"
      :characteristics="bookCharacteristics"
      :genre="genre"
    />

    <BookDescription v-if="book.longEditorReview" :book="book" />

    <div v-if="secondQuote" :class="bgClassWithGenre">
      <BaseQuote :quote="secondQuote" second-quote-variant />
    </div>

    <div :class="{ [bgClassWithGenre]: !secondQuote }">
      <ReviewsSection :book="book" />
    </div>

    <div :class="{ [bgClassWithGenre]: secondQuote }">
      <RelatedBooksCarousel :slug="book.slug" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@include generate-genre-classes(
  'background-color',
  'light',
  'background-section',
  0.5
);

.alert-warning {
  margin-bottom: 2px !important;
  padding-left: 0;
  padding-right: 0;

  :deep(.container) {
    justify-content: center;
  }
}
</style>
